'use client'

import { ArrowDown, ArrowRight } from 'lucide-react'
import { useId, useState, type KeyboardEvent } from 'react'
import { PlaySampleButton } from '@/components/santa-video/landing/sample-video'
import { SECTION_LEAD, SECTION_TITLE } from '@/components/santa-video/landing/section-classes'
import type { StoryProof } from '@/lib/santa-video/landing-model'
import type { SampleProofRow } from '@/lib/santa-video/order-page-copy'
import { cn } from '@/lib/utils'

export interface ProofLabels {
  title: string; lead: string
  filledLabel: string; saidLabel: string
}

function ProofRow({ row }: { row: SampleProofRow }) {
  return (
    <li className="grid items-center gap-3 md:grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1.35fr)] md:gap-5">
      <div className="rounded-2xl bg-white px-5 py-3 text-stone-900 shadow-lg">
        <span className="block text-sm text-stone-500">{row.label}</span>
        <span className="font-semibold">{row.value}</span>
      </div>
      <ArrowRight aria-hidden className="hidden h-6 w-6 text-amber-200 md:block" />
      <ArrowDown aria-hidden className="mx-auto h-5 w-5 text-amber-200 md:hidden" />
      <blockquote className="text-center text-xl leading-snug text-white md:text-left md:text-2xl">
        <span aria-hidden className="text-amber-200">«</span>{row.quote}<span aria-hidden className="text-amber-200">»</span>
      </blockquote>
    </li>
  )
}

function ProofPanel({ story, proof, labels }: StoryProof & { labels: ProofLabels }) {
  return (
    <>
      <p className="text-center text-lg text-white/85">{proof.intro}</p>
      <div aria-hidden className="mt-8 hidden grid-cols-[minmax(0,1fr)_1.5rem_minmax(0,1.35fr)] gap-5 text-sm font-semibold text-white/70 md:grid">
        <span className="pl-5">{labels.filledLabel}</span><span /><span>{labels.saidLabel}</span>
      </div>
      <ol className="mt-6 flex flex-col gap-8 md:mt-3 md:gap-5">
        {proof.rows.map((row) => <ProofRow key={row.label} row={row} />)}
      </ol>
      <div className="mt-10 flex justify-center"><PlaySampleButton label={proof.watch} story={story} /></div>
    </>
  )
}

const ARROW_STEP: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowLeft: -1 }

/**
 * La barra de pestañas, con el patrón de la APG: flechas para moverse (en
 * bucle) y el foco sigue a la selección; solo la seleccionada es tabulable.
 */
function ProofTabList({ proofs, selected, onSelect, tabId, panelId }: {
  proofs: readonly StoryProof[]
  selected: number
  onSelect: (index: number) => void
  tabId: (index: number) => string
  panelId: string
}) {
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const step = ARROW_STEP[event.key]
    if (!step) return
    event.preventDefault()
    const next = (selected + step + proofs.length) % proofs.length
    onSelect(next)
    document.getElementById(tabId(next))?.focus()
  }

  return (
    <div className="mt-8 flex justify-center">
      <div className="inline-flex rounded-full border border-white/30 bg-black/20 p-1" role="tablist">
        {proofs.map(({ story, proof }, index) => (
          <button
            aria-controls={panelId}
            aria-selected={index === selected}
            className={cn(
              'rounded-full px-5 py-2 text-base font-semibold transition-colors motion-reduce:transition-none',
              index === selected ? 'bg-amber-200 text-red-900' : 'text-white/85 hover:text-white',
            )}
            id={tabId(index)}
            key={story}
            onClick={() => onSelect(index)}
            onKeyDown={onKeyDown}
            role="tab"
            tabIndex={index === selected ? 0 : -1}
            type="button"
          >
            {proof.tab}
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * El corazón de la landing: lo que se escribió en el formulario de cada pedido
 * de ejemplo frente a lo que Papá Noel dijo con ello. Con dos historias, una
 * pestaña por cada una (en el orden de las fichas); con una, sin pestañas.
 * Las tarjetas blancas son la misma superficie clara del wizard: lo siguiente
 * que verá el padre.
 */
export function ProofSection({ proofs, labels }: { proofs: readonly StoryProof[]; labels: ProofLabels }) {
  const [selected, setSelected] = useState(0)
  const baseId = useId()
  const tabId = (index: number) => `${baseId}-tab-${index}`
  const panelId = `${baseId}-panel`
  const tabbed = proofs.length > 1
  const current = proofs[selected] ?? proofs[0]
  if (!current) return null

  return (
    <section aria-labelledby="sample-proof-title" className="mx-auto w-full max-w-5xl">
      <h2 className={SECTION_TITLE} id="sample-proof-title">{labels.title}</h2>
      <p className={SECTION_LEAD}>{labels.lead}</p>
      {tabbed && <ProofTabList onSelect={setSelected} panelId={panelId} proofs={proofs} selected={selected} tabId={tabId} />}
      <div aria-labelledby={tabbed ? tabId(selected) : undefined} className="mt-8" id={panelId} role={tabbed ? 'tabpanel' : undefined}>
        <ProofPanel key={current.story} labels={labels} proof={current.proof} story={current.story} />
      </div>
    </section>
  )
}
